import { $, alcance, aviso, esc, fecha, limpiar, pedir, sb } from './nucleo';

// =====================================================================
//  LA ACTIVIDAD  (0046, D94, D95)
// =====================================================================
//  Quién entró al sistema y qué cambió cada uno. Esta pantalla no anota nada:
//  lo anota la BASE, con un trigger sobre `auth.sessions` (ingresos) y otro en
//  cada tabla (cambios). Acá sólo se lee, de tres vistas:
//   · `v_actividad_resumen` — una fila por persona;
//   · `v_ingresos`          — cada login, con su última señal y su final;
//   · `v_actividad`         — cada alta, cambio y baja.
//
//  La abre `auditoria.ver`. El ALCANCE importa (D85): el rastro de cambios se
//  lee sólo con alcance `todos` (guarda filas enteras, costos incluidos). Con
//  `propio` se ven los ingresos propios y la pantalla DICE que no hay rastro,
//  no "cero cambios".
//
//  Todo se guarda un PLAZO (D95), CONFIGURABLE: `actividad.meses_guardado`. Los
//  textos de vacío lo LEEN (no un "último mes" fijo). Si no se pudo leer, no se
//  inventa ninguno (D83).

const TOPE = 100;
let TOPE_ING = TOPE, TOPE_CAM = TOPE;
let PERSONAS: { id: number; nombre: string }[] = [];
let ERROR_PERSONAS: string | null = null;
//  Cada lectura lleva su número: la vieja no pinta encima de la nueva.
let VUELTA_ING = 0, VUELTA_CAM = 0, VUELTA_RES = 0;

const todos = () => alcance('auditoria', 'ver') === 'todos';

//  CUÁNTO SE GUARDA. `config` la lee cualquier usuario activo
//  (`app.es_usuario()`): un error es siempre una falla de verdad. Una lectura
//  por sesión; si falló, volver a la pestaña es el reintento.
//
//  D83: si no se pudo leer, el texto NO cae en "el último mes": afirmaría un
//  plazo que nadie leyó en los cinco lugares donde la frase dice qué NO hay.
let MESES: number | null = null;
let PEDIDO_MESES: Promise<void> | null = null;
const leerMeses = async () => {
  const { data, error } = await pedir(sb.from('config').select('valor')
    .eq('clave', 'actividad.meses_guardado').maybeSingle());
  const n = Number((data as { valor?: unknown } | null)?.valor);
  //  Tres respuestas: si la consulta anduvo y la fila NO ESTÁ, el plazo ES un
  //  mes (`app.purgar_actividad` hace `greatest(coalesce(nullif(…)::int, 1), 1)`),
  //  no "no se sabe". `null` es sólo el error de verdad, el que se reintenta.
  MESES = error ? null : (Number.isFinite(n) && n > 0 ? n : 1);
};
const meses = () => (PEDIDO_MESES ??= leerMeses()
  .then(() => { if (MESES == null) PEDIDO_MESES = null; }));
//  El plazo, dicho como se dice. En singular cuando es uno, y sin número
//  cuando no se sabe.
const plazo = () => MESES == null ? 'el período que se guarda'
                  : MESES === 1   ? 'el último mes'
                                  : 'los últimos ' + MESES + ' meses';

//  Los nombres de las tablas, dichos como los dice la empresa. Lo que no
//  está acá se muestra con su nombre técnico: mejor eso que inventar.
const TABLAS: Record<string, string> = {
  pedidos: 'Pedido', pedido_items: 'Ítem de pedido', pedido_pagos: 'Cobro',
  pedido_eventos: 'Historial de pedido', clientes: 'Cliente', productos: 'Producto',
  precios: 'Precio', unidades: 'Equipo', movimientos_stock: 'Movimiento de stock',
  movimientos_caja: 'Movimiento de caja', sesiones_caja: 'Sesión de caja',
  arqueos: 'Arqueo', transferencias_caja: 'Bolsín', desvios: 'Desvío',
  cc_movimientos: 'Cuenta corriente', vueltas: 'Vuelta', vuelta_pedidos: 'Parada',
  rendiciones: 'Rendición', liquidaciones: 'Liquidación', usuarios: 'Usuario',
  usuario_roles: 'Rol de usuario', usuario_permisos: 'Permiso de usuario',
  config: 'Configuración', ordenes_compra: 'Orden de compra',
  recepciones: 'Recepción', conteos: 'Conteo', conteo_lineas: 'Línea de conteo',
  aprobaciones: 'Aprobación', cotizaciones: 'Cotización', proveedores: 'Proveedor',
  esquemas_comision: 'Tarifa de comisión', notificaciones: 'Aviso',
  marcas: 'Marca', categorias: 'Categoría', subcategorias: 'Subcategoría',
  lineas: 'Línea', colores: 'Color', zonas: 'Zona', tarifas_envio: 'Tarifa de envío',
  cajas: 'Caja', caja_usuarios: 'Habilitación de caja', roles: 'Rol',
  rol_permisos: 'Permiso de rol', medios_pago: 'Medio de pago',
  metodos_entrega: 'Método de entrega', orden_items: 'Línea de orden de compra',
  recepcion_items: 'Ítem de recepción', rendicion_montos: 'Monto de rendición',
  liquidacion_items: 'Ítem de liquidación', cliente_eventos: 'Historial de cliente',
  usuario_eventos: 'Historial de usuario', orden_eventos: 'Historial de orden',
  cc_credito_historial: 'Historial de crédito', comision_esquema_historial: 'Historial de tarifas',
  vuelta_resultados: 'Resultado de parada', transiciones_estado: 'Circuito',
  estados_item: 'Estado', reglas_notificacion: 'Regla de aviso',
  politicas_descuento: 'Política de descuento', rangos_horarios: 'Rango horario',
  tipos_aprobacion: 'Tipo de aprobación', monedas: 'Moneda', permisos: 'Permiso',
  //  Las dos de la plata del proveedor (0050 y 0051). Su rastro sale con los
  //  montos tapados para quien no ve la plata (D114): acá sólo se nombra la
  //  tabla, que es lo que dice QUÉ se tocó.
  proveedor_movimientos: 'Cuenta de proveedor', costo_cambios: 'Cambio de costo',
};
const nombreTabla = (t: string | null) => t ? (TABLAS[t] || t) : '—';

const ACCION: Record<string, string> = { alta: 'alta', cambio: 'cambio', baja: 'baja' };
const CHIP: Record<string, string> = { alta: 'c-ok', cambio: 'c-pen', baja: 'c-bad' };

//  "hace 12 min". La hora exacta va en el `title`.
export const haceCuanto = (t: string | null | undefined, ahora = Date.now()) => {
  if (!t) return '—';
  const min = Math.round((ahora - new Date(t).getTime()) / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h ${min % 60 ? (min % 60) + ' min' : ''}`.trim();
  const d = Math.floor(h / 24);
  return `hace ${d} día${d === 1 ? '' : 's'}`;
};
const cuando = (t: string | null | undefined) =>
  t ? `<span title="${esc(fecha(t))}">${esc(haceCuanto(t))}</span>` : '—';

//  La sesión renueva su token ~una vez por hora con la pestaña abierta: más de
//  90 minutos sin señal (cerró sin salir, o se durmió la máquina) no afirma que "está".
export const estadoSesion = (señal: string | null, fin: string | null, ahora = Date.now()) => {
  if (fin) return `<span class="chip c-bad">cerró</span> <span class="hint">${esc(haceCuanto(fin, ahora))}</span>`;
  if (señal && ahora - new Date(señal).getTime() <= 90 * 60000)
    return `<span class="chip c-ok">abierta</span>`;
  return `<span class="chip c-warn">sin señal</span> <span class="hint">${
    señal ? 'desde ' + esc(haceCuanto(señal, ahora)) : ''}</span>`;
};

//  El navegador, corto: el `user_agent` entero no entra en una celda.
const navegador = (ua: string | null) => {
  if (!ua) return '—';
  const n = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari' : 'Otro';
  const so = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iPhone' : /Mac OS/.test(ua) ? 'Mac'
    : /Linux/.test(ua) ? 'Linux' : '';
  return `<span title="${esc(ua)}">${esc(n)}${so ? ' · ' + esc(so) : ''}</span>`;
};

const valor = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '<i class="hint">vacío</i>';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return esc(s.length > 80 ? s.slice(0, 80) + '…' : s);
};

//  Qué cambió. En un cambio la base guarda SÓLO los campos que cambiaron, así
//  que se muestran todos. En un alta o una baja guarda la fila entera: se
//  muestran unos pocos campos y el resto queda a un click.
export const detalle = (accion: string | null, antes: any, despues: any) => {
  if (accion === 'cambio'){
    const d = despues || {};
    const claves = Object.keys(d);
    //  En una tabla sin `id` el "antes" trae la fila entera, para saber cuál
    //  fue. Lo que cambió es lo que está en el "después".
    return claves.map(k =>
      `<div><b>${esc(k)}</b>: ${valor(antes?.[k])} → ${valor(d[k])}</div>`).join('')
      || '<i class="hint">sin detalle</i>';
  }
  const fila = (accion === 'baja' ? antes : despues) || {};
  const claves = Object.keys(fila).filter(k => fila[k] !== null && k !== 'id');
  const primeras = claves.slice(0, 4).map(k =>
    `<span class="mono">${esc(k)}</span>=${valor(fila[k])}`).join(' · ');
  if (claves.length <= 4) return primeras || '<i class="hint">sin detalle</i>';
  return `${primeras}<details><summary class="hint">ver los ${claves.length} campos</summary>${
    claves.map(k => `<div><b>${esc(k)}</b>: ${valor(fila[k])}</div>`).join('')}</details>`;
};

//  En una tabla sin `id` (config, usuario_roles, rol_permisos…) la base
//  guarda en el "antes" la fila entera, justamente para saber CUÁL fue. Se
//  muestran sus claves: `clave`, `codigo` (`tipos_aprobacion`) y las columnas
//  `…_id` que no cambiaron.
export const cualFila = (accion: string | null, antes: any, despues: any) => {
  const fila = (accion === 'alta' ? despues : antes) || {};
  const cambiadas = accion === 'cambio' ? Object.keys(despues || {}) : [];
  const claves = Object.keys(fila).filter(k =>
    (k === 'clave' || k === 'codigo' || k.endsWith('_id')) && !cambiadas.includes(k) && fila[k] !== null);
  return claves.length
    ? ' <span class="mono">' + claves.map(k => `${esc(k)}=${valor(fila[k])}`).join(' · ') + '</span>'
    : '';
};

//  El filtro de período, en fecha de inicio. `null` = todo lo que hay (el plazo que se guarda).
const desde = (sel: string) => {
  const v = $(sel).value;
  const ahora = Date.now();
  if (v === '1h')  return new Date(ahora - 3600e3).toISOString();
  if (v === '24h') return new Date(ahora - 24 * 3600e3).toISOString();
  if (v === '7d')  return new Date(ahora - 7 * 24 * 3600e3).toISOString();
  if (v === 'hoy'){ const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); }
  return null;
};

// ---------------------------------------------------------------------
//  Las personas, para los dos filtros
// ---------------------------------------------------------------------
async function cargarPersonas(){
  const { data, error } = await pedir(sb.from('usuarios').select('id,nombre').order('nombre'));
  ERROR_PERSONAS = error ? error.message : null;
  PERSONAS = error ? [] : ((data || []) as any);
  for (const sel of ['#acIngPersona', '#acCamPersona']){
    const s = $(sel);
    //  Lo elegido se conserva AUNQUE ya no esté en la lista (no se pudo leer, o
    //  vino de "Ver sus cambios"): un select que vuelve a "todas" contradice la tabla.
    const antes = s.value;
    const textoAntes = s.selectedOptions?.[0]?.textContent || ('#' + antes);
    s.innerHTML = '<option value="">Todas las personas</option>' +
      (sel === '#acCamPersona' ? '<option value="sistema">El sistema (sin persona)</option>' : '') +
      PERSONAS.map(p => `<option value="${p.id}">${esc(p.nombre)}</option>`).join('');
    if (antes && ![...s.options].some(o => o.value === antes))
      s.insertAdjacentHTML('beforeend', `<option value="${esc(antes)}">${esc(textoAntes)}</option>`);
    s.value = antes;
  }
  if (ERROR_PERSONAS)
    aviso('#acPersonasMsg', 'bad', 'No se pudo leer la lista de personas (' + ERROR_PERSONAS +
      '), así que los filtros sólo ofrecen "todas". Volvé a entrar a la pestaña para reintentar.');
  else limpiar('#acPersonasMsg');
}

// ---------------------------------------------------------------------
//  El resumen
// ---------------------------------------------------------------------
async function cargarResumen(){
  const vuelta = ++VUELTA_RES;
  $('#tblAcResumen').innerHTML = '<tr><td colspan="7" class="vacio">Leyendo…</td></tr>';
  const { data, error } = await pedir(sb.from('v_actividad_resumen').select('*')
    .order('ultimo_ingreso', { ascending: false, nullsFirst: false }));
  if (vuelta !== VUELTA_RES) return;
  if (error){
    $('#tblAcResumen').innerHTML = '';
    aviso('#acResumenMsg', 'bad', 'No se pudo leer el resumen (' + error.message +
      '). No quiere decir que nadie haya entrado.');
    return;
  }
  limpiar('#acResumenMsg');
  const t = todos();
  const filas = (data || []) as any[];
  $('#tblAcResumen').innerHTML = filas.map(r => `
    <tr>
      <td><b>${esc(r.nombre)}</b></td>
      <td>${cuando(r.ultimo_ingreso)}</td>
      <td>${r.ultimo_ingreso ? estadoSesion(r.ultima_senal, r.terminada_en) : '—'}</td>
      <td class="mono">${t ? (r.cambios_desde_ingreso ?? '—') : '<span class="hint">no lo podés ver</span>'}</td>
      <td class="mono">${t ? (r.cambios_24h ?? 0) : '<span class="hint">no lo podés ver</span>'}</td>
      <td>${t ? cuando(r.ultimo_cambio) : '<span class="hint">no lo podés ver</span>'}</td>
      <td>${t ? '<button class="b b-sec" type="button" data-ver="' + r.usuario_id + '">Ver sus cambios</button>' : ''}</td>
    </tr>`).join('') ||
    `<tr><td colspan="7" class="vacio">${t
      ? 'Ninguna persona dada de alta entró ni cambió nada en ' + plazo() + '.'
      : 'No entraste en ' + plazo() + ' (y sólo podés ver lo tuyo).'}</td></tr>`;
  $('#tblAcResumen').querySelectorAll('button[data-ver]').forEach((b: any) => b.onclick = () => {
    const s = $('#acCamPersona');
    const id = b.dataset.ver;
    if (![...s.options].some(o => o.value === id)){
      //  La lista de personas no se pudo leer (o esa persona ya no está):
      //  se agrega la opción para que el filtro diga a quién se está mirando.
      const nombre = b.closest('tr').querySelector('td b')?.textContent || ('#' + id);
      s.insertAdjacentHTML('beforeend', `<option value="${esc(id)}">${esc(nombre)}</option>`);
    }
    s.value = id;
    TOPE_CAM = TOPE;
    cargarCambios();
    $('#cardAcCambios').scrollIntoView?.({ block: 'start' });
  });
}

// ---------------------------------------------------------------------
//  Los ingresos
// ---------------------------------------------------------------------
async function cargarIngresos(){
  const vuelta = ++VUELTA_ING;
  $('#tblAcIngresos').innerHTML = '<tr><td colspan="6" class="vacio">Leyendo…</td></tr>';
  limpiar('#acIngMas');
  const persona = $('#acIngPersona').value;
  const d = desde('#acIngPeriodo');
  let q = sb.from('v_ingresos').select('*');
  if (persona) q = q.eq('usuario_id', Number(persona));
  if (d) q = q.gte('ocurrido_en', d);
  const { data, error } = await pedir(q.order('ocurrido_en', { ascending: false }).limit(TOPE_ING + 1));
  if (vuelta !== VUELTA_ING) return;
  if (error){
    $('#tblAcIngresos').innerHTML = '';
    aviso('#acIngMsg', 'bad', 'No se pudieron leer los ingresos (' + error.message +
      '). No quiere decir que nadie haya entrado.');
    return;
  }
  limpiar('#acIngMsg');
  const todas = (data || []) as any[];
  const filas = todas.slice(0, TOPE_ING);
  const filtrado = !!(persona || d);
  $('#tblAcIngresos').innerHTML = filas.map(r => `
    <tr>
      <td>${cuando(r.ocurrido_en)}<div class="hint">${esc(fecha(r.ocurrido_en))}</div></td>
      <td>${r.usuario_id != null
        ? '<b>' + esc(r.nombre) + '</b>'
        : '<span class="chip c-bad">sin alta</span> ' + esc(r.email ?? '—')}</td>
      <td class="mono">${esc(r.ip ?? '—')}</td>
      <td>${navegador(r.navegador)}</td>
      <td>${cuando(r.ultima_senal)}</td>
      <td>${estadoSesion(r.ultima_senal, r.terminada_en)}</td>
    </tr>`).join('') ||
    `<tr><td colspan="6" class="vacio">${filtrado
      ? 'Ningún ingreso coincide con el filtro.'
      : todos() ? 'Nadie entró en ' + plazo() + '.'
                : 'No hay ingresos tuyos en ' + plazo() + ' (y sólo podés ver los tuyos).'}</td></tr>`;
  if (todas.length > TOPE_ING)
    $('#acIngMas').innerHTML = `<button class="b b-sec" type="button" id="btnAcIngMas">Mostrar ${TOPE} más</button>`;
  const b = $('#btnAcIngMas');
  if (b) b.onclick = () => { TOPE_ING += TOPE; cargarIngresos(); };
}

// ---------------------------------------------------------------------
//  Los cambios
// ---------------------------------------------------------------------
async function cargarCambios(){
  const vuelta = ++VUELTA_CAM;
  limpiar('#acCamMas');
  //  Con alcance `propio` la base no devuelve NINGUNA fila del rastro (ni
  //  las propias: traen costos). Consultar daría una lista vacía que se
  //  leería como "no cambiaste nada". Se dice lo que pasa y no se consulta.
  if (!todos()){
    $('#tblAcCambios').innerHTML = '';
    $('#formAcCambios').classList.add('oculto');
    aviso('#acCamMsg', 'info', 'El rastro de cambios lo ve sólo quien tiene el permiso ' +
      'de auditoría sobre todos: guarda las filas enteras, costos incluidos. ' +
      'Tu permiso alcanza sólo tus ingresos.');
    return;
  }
  $('#formAcCambios').classList.remove('oculto');
  $('#tblAcCambios').innerHTML = '<tr><td colspan="4" class="vacio">Leyendo…</td></tr>';
  const persona = $('#acCamPersona').value;
  const tabla = $('#acCamTabla').value;
  const accion = $('#acCamAccion').value;
  const d = desde('#acCamPeriodo');
  let q = sb.from('v_actividad').select('*').in('accion', ['alta', 'cambio', 'baja']);
  if (persona === 'sistema') q = q.is('usuario_id', null);
  else if (persona) q = q.eq('usuario_id', Number(persona));
  if (tabla) q = q.eq('tabla', tabla);
  if (accion) q = q.eq('accion', accion);
  if (d) q = q.gte('ocurrido_en', d);
  const { data, error } = await pedir(q.order('id', { ascending: false }).limit(TOPE_CAM + 1));
  if (vuelta !== VUELTA_CAM) return;
  if (error){
    $('#tblAcCambios').innerHTML = '';
    aviso('#acCamMsg', 'bad', 'No se pudieron leer los cambios (' + error.message +
      '). No quiere decir que nadie haya cambiado nada.');
    return;
  }
  limpiar('#acCamMsg');
  const todas = (data || []) as any[];
  const filas = todas.slice(0, TOPE_CAM);
  const filtrado = !!(persona || tabla || accion || d);
  $('#tblAcCambios').innerHTML = filas.map(r => `
    <tr>
      <td>${cuando(r.ocurrido_en)}<div class="hint">${esc(fecha(r.ocurrido_en))}</div></td>
      <td>${r.usuario_id != null
        ? '<b>' + esc(r.nombre) + '</b>'
        : '<span class="hint">el sistema</span>'}</td>
      <td><span class="chip ${CHIP[r.accion] || ''}">${esc(ACCION[r.accion] || r.accion)}</span>
          ${esc(nombreTabla(r.tabla))}${r.registro_id != null
            ? ' <span class="mono">#' + esc(r.registro_id) + '</span>'
            : cualFila(r.accion, r.datos_antes, r.datos_despues)}</td>
      <td class="det">${detalle(r.accion, r.datos_antes, r.datos_despues)}</td>
    </tr>`).join('') ||
    `<tr><td colspan="4" class="vacio">${filtrado
      ? 'Ningún cambio coincide con el filtro.'
      : 'Nadie cambió nada en ' + plazo() + '.'}</td></tr>`;
  if (todas.length > TOPE_CAM)
    $('#acCamMas').innerHTML = `<button class="b b-sec" type="button" id="btnAcCamMas">Mostrar ${TOPE} más</button>`;
  const b = $('#btnAcCamMas');
  if (b) b.onclick = () => { TOPE_CAM += TOPE; cargarCambios(); };
}

// ---------------------------------------------------------------------
//  La pestaña
// ---------------------------------------------------------------------
let ARMADA = false;
function armar(){
  if (ARMADA) return;
  ARMADA = true;
  $('#acCamTabla').innerHTML =
    '<option value="">Todas las tablas</option>' +
    Object.entries(TABLAS).sort((a, b) => a[1].localeCompare(b[1], 'es'))
      .map(([k, v]) => `<option value="${k}">${esc(v)}</option>`).join('');
  for (const sel of ['#acIngPersona', '#acIngPeriodo'])
    $(sel).onchange = () => { TOPE_ING = TOPE; cargarIngresos(); };
  for (const sel of ['#acCamPersona', '#acCamTabla', '#acCamAccion', '#acCamPeriodo'])
    $(sel).onchange = () => { TOPE_CAM = TOPE; cargarCambios(); };
}

//  Sin guarda de permiso a propósito: la llaman `entrar()` (ya dentro de
//  `if (puede('auditoria','ver'))`) y el click en la pestaña (oculta sin ese
//  permiso). Regla del 13/9.
export async function cargarActividad(){
  armar();
  TOPE_ING = TOPE; TOPE_CAM = TOPE;
  //  Ojo: LOS TOGGLES DE PERMISO VAN PRIMERO, antes de todo `await`; si no, con
  //  la red caída la pestaña muestra el filtro "Persona" y esconde el cartel
  //  del alcance.
  $('#acAlcance').classList.toggle('oculto', todos());
  //  Con alcance propio sólo se ven los ingresos propios: ofrecer elegir a
  //  otra persona terminaría en "ningún ingreso coincide", que no es cierto.
  $('#acIngPersonaLbl').classList.toggle('oculto', !todos());
  //  El plazo se espera ANTES de largar las cuatro cargas: cada una pinta su
  //  texto de vacío y en paralelo pintarían "no se sabe" sin corregirse.
  await meses();
  //  Tres lugares dicen el plazo (los dos "Todo (…)" de los filtros y el renglón
  //  del encabezado): actualizar los tres.
  for (const sel of ['#acIngPeriodo', '#acCamPeriodo']){
    const o = $(sel).querySelector('option[value=""]');
    if (o) o.textContent = 'Todo (' + plazo() + ')';
  }
  $('#acPlazoHint').textContent = MESES == null
    ? 'Se guarda un tiempo, y no se pudo leer cuánto.'
    : MESES === 1 ? 'Se guarda un mes.' : 'Se guardan ' + MESES + ' meses.';
  //  Las cuatro en paralelo: la lista de personas sólo llena los filtros, y
  //  una lectura lenta no tiene por qué frenar las otras tres.
  cargarPersonas();
  cargarResumen();
  cargarIngresos();
  cargarCambios();
}
